import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { clubApi, fmtEur, fmtDate } from "../../lib/clubApi";
import { toast } from "sonner";

// Home dashboard: recent activity + at-a-glance figures below tabs.
export default function ClubDashboard() {
    const [data, setData] = useState(null);
    const [sales, setSales] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([clubApi.dashboard(), clubApi.listSales(8)])
            .then(([d, s]) => { setData(d); setSales(s); })
            .catch(() => toast.error("Errore caricamento"))
            .finally(() => setLoading(false));
    }, []);

    if (loading) {
        return <div className="grid place-items-center py-10 text-stone-500"><Loader2 className="animate-spin" /></div>;
    }
    if (!data) return null;

    return (
        <div className="space-y-5" data-testid="club-dashboard">
            <div>
                <h2 className="font-display font-bold text-2xl text-stone-900">Riepilogo</h2>
                <p className="text-sm text-stone-500 mt-1">Tessere e vendite del negozio.</p>
            </div>

            <div className="grid grid-cols-3 gap-2 text-center">
                <MiniStat label="Totale soci" value={data.total_members} />
                <MiniStat label="In scadenza" value={data.expiring_members} accent="amber" />
                <MiniStat label="Scaduti" value={data.expired_members} accent="red" />
            </div>

            <div className="rounded-2xl border border-stone-200 p-4 bg-stone-50">
                <p className="text-sm text-stone-500">Scontrino medio</p>
                <p className="text-xl font-display font-bold text-stone-900">{fmtEur(data.avg_ticket)}</p>
                <p className="text-xs text-stone-500 mt-0.5">{data.total_visits} visite totali · {fmtEur(data.total_member_sales)} incassato</p>
            </div>

            {sales.length > 0 && (
                <div>
                    <h3 className="font-display font-bold text-lg mb-2 text-stone-900">Ultime vendite</h3>
                    <ul className="divide-y divide-stone-200 rounded-2xl border border-stone-200 overflow-hidden">
                        {sales.map((s) => (
                            <li key={s.id} className="flex items-center justify-between px-4 py-3 bg-white" data-testid={`recent-sale-${s.id}`}>
                                <div className="min-w-0">
                                    <p className="font-semibold text-stone-900 truncate">{s.member_name || "—"}</p>
                                    <p className="text-xs text-stone-500">{fmtDate(s.date)}</p>
                                </div>
                                <span className="font-bold text-stone-900 tabular-nums">{fmtEur(s.amount)}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {data.inactive_members && data.inactive_members.length > 0 && (
                <div>
                    <h3 className="font-display font-bold text-lg mb-2 text-stone-900">Non vengono da un po'</h3>
                    <ul className="space-y-2">
                        {data.inactive_members.slice(0, 5).map((m) => (
                            <li key={m.id}>
                                <Link
                                    to={`/club/clienti/${m.id}`}
                                    className="flex items-center justify-between rounded-xl border border-stone-200 bg-white px-4 py-3 active:bg-stone-50"
                                    data-testid={`inactive-${m.id}`}
                                >
                                    <div className="min-w-0">
                                        <p className="font-semibold text-stone-900 truncate">{m.name}</p>
                                        <p className="text-xs text-stone-500">{m.phone}</p>
                                    </div>
                                    <span className="text-xs bg-stone-900 text-white font-bold px-2 py-1 rounded-full">
                                        {m.days_since >= 999 ? "Mai" : `${m.days_since}g`}
                                    </span>
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

function MiniStat({ label, value, accent }) {
    const cls = accent === "amber" ? "text-amber-700" : accent === "red" ? "text-red-700" : "text-stone-900";
    return (
        <div className="rounded-xl border border-stone-200 py-3 bg-white">
            <p className={`text-2xl font-display font-bold ${cls}`}>{value}</p>
            <p className="text-[11px] text-stone-500 mt-0.5 leading-tight">{label}</p>
        </div>
    );
}
